// Session-memory cache: never persisted to shared browser storage. Switching
// accounts drops all entries; an old request cannot publish into the new cache.
const FRESH_MS = 120000;
let owner = null;
const entries = new Map();
const EMPTY = { value: null, loading: false, error: false, updatedAt: 0 };
export function invalidateUsageCache(accountId) {
  if (owner === String(accountId)) entries.forEach(entry => { entry.snapshot = { ...entry.snapshot, updatedAt: 0 }; });
}
export function clearUsageCache() { owner = null; entries.clear(); }
function entryFor(accountId, key) {
  if (!accountId) { clearUsageCache(); return null; }
  if (owner !== String(accountId)) { clearUsageCache(); owner = String(accountId); }
  if (!entries.has(key)) {
    if (entries.size >= 40) {
      const victim = [...entries].find(([, entry]) => !entry.pending && !entry.listeners.size);
      if (victim) entries.delete(victim[0]);
    }
    entries.set(key, { snapshot: EMPTY, listeners: new Set(), pending: null });
  }
  return entries.get(key);
}
export function readUsageCache(accountId, key) { return entryFor(accountId, key)?.snapshot || EMPTY; }
export function subscribeUsageCache(accountId, key, listener) {
  const entry = entryFor(accountId, key);
  if (!entry) return () => {};
  entry.listeners.add(listener);
  return () => entry.listeners.delete(listener);
}
export function loadCachedUsage(accountId, key, loader, { force = false } = {}) {
  const entry = entryFor(accountId, key);
  if (!entry) return Promise.reject(new Error('Account required'));
  if (entry.pending) return entry.pending;
  if (!force && entry.snapshot.updatedAt && Date.now() - entry.snapshot.updatedAt < FRESH_MS) return Promise.resolve(entry.snapshot.value);
  const publish = patch => {
    if (owner !== String(accountId) || entries.get(key) !== entry) return;
    entry.snapshot = { ...entry.snapshot, ...patch };
    entry.listeners.forEach(listener => listener(entry.snapshot));
  };
  publish({ loading: true, error: false });
  entry.pending = Promise.resolve().then(() => loader(partial => {
    // Keep a complete cached view visible during background revalidation.
    if (!entry.snapshot.value || entry.snapshot.value.partial) publish({ value: partial });
  })).then(value => {
    publish({ value, loading: false, error: false, updatedAt: Date.now() });
    return value;
  }).catch(error => {
    publish({ loading: false, error: true });
    throw error;
  }).finally(() => { entry.pending = null; });
  return entry.pending;
}
